import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/contexts/AuthContext";
import { PageHeader, PageBody, ErrorBlock } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, Search, Download, Filter, AlertTriangle } from "lucide-react";

export default function MarketplacePage() {
  const [q, setQ] = useState("");
  const [minCommission, setMinCommission] = useState(0);
  const [maxVoc, setMaxVoc] = useState(1.5);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [shop, setShop] = useState(null);

  const search = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/marketplace/tiktok-shop/products`, {
        params: { q, min_commission: minCommission, max_voc: maxVoc,
                  show_ineligible: showIneligible, limit: 24 },
      });
      setItems(r.data.items || []);
      setShop(r.data.shop || null);
    } catch (e) {
      setErr(e?.response?.data || { error: "search_failed", detail: String(e) });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, minCommission, maxVoc, showIneligible]);

  useEffect(() => { search(); /* initial empty search */ }, []);  // eslint-disable-line

  const importProduct = async (p) => {
    try {
      const r = await axios.post(`${API}/marketplace/tiktok-shop/products/${p.product_id}/import`);
      toast.success(`Imported: ${r.data.title}`);
      if (r.data.warning) toast.warning(r.data.warning);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Import failed");
    }
  };

  return (
    <>
      <PageHeader
        icon={ShoppingBag}
        title="TikTok Shop Marketplace"
        subtitle={shop ? `Active shop: ${shop.name}` : "Connect a TikTok Shop in Settings to browse affiliate products"}
        testid="marketplace-header"
      />
      <PageBody>
        <form onSubmit={(e) => { e.preventDefault(); search(); }} className="ap-panel p-4 space-y-3" data-testid="marketplace-search-form">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products (e.g. earbuds, lip gloss, kitchen)"
                className="pl-9 bg-zinc-900 border-zinc-700 text-zinc-100"
                data-testid="marketplace-search-input"
              />
            </div>
            <Button type="submit" disabled={loading}
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
                    data-testid="marketplace-search-btn">
              {loading ? "Searching…" : "Search"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowAdvanced((v) => !v)}
                    className="border-zinc-700 bg-zinc-900 text-zinc-200" data-testid="marketplace-advanced-btn">
              <Filter className="h-4 w-4" />
            </Button>
          </div>
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-zinc-800">
              <label className="text-xs text-zinc-400 space-y-1">
                Min commission: <span className="text-emerald-400 mono">{minCommission}%</span>
                <input type="range" min={0} max={50} value={minCommission} onChange={(e) => setMinCommission(parseFloat(e.target.value))}
                       className="w-full" data-testid="filter-min-commission" />
              </label>
              <label className="text-xs text-zinc-400 space-y-1">
                Max VoC index: <span className="text-amber-400 mono">{maxVoc.toFixed(1)}</span>
                <input type="range" min={0.5} max={3.0} step={0.1} value={maxVoc} onChange={(e) => setMaxVoc(parseFloat(e.target.value))}
                       className="w-full" data-testid="filter-max-voc" />
              </label>
              <label className="text-xs text-zinc-400 inline-flex items-center gap-2 mt-3">
                <input type="checkbox" checked={showIneligible} onChange={(e) => setShowIneligible(e.target.checked)}
                       data-testid="filter-show-ineligible" />
                Show ineligible (VoC > 1.5)
              </label>
            </div>
          )}
        </form>

        {err && <ErrorBlock data={err} testid="marketplace-error" />}

        {items.length === 0 && !loading && !err && (
          <div className="ap-panel p-8 text-center" data-testid="marketplace-empty">
            <ShoppingBag className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
            <div className="text-sm text-zinc-400">No products found.</div>
            <div className="text-xs text-zinc-600 mt-1">Try a different keyword, or connect/re-authorize a TikTok Shop in Settings.</div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="marketplace-grid">
          {items.map((p) => (
            <div key={p.product_id} className="ap-panel p-3 flex flex-col gap-2" data-testid={`marketplace-product-${p.product_id}`}>
              {p.image_url ? (
                <img src={p.image_url} alt="" className="w-full aspect-square object-cover rounded-md bg-zinc-900" />
              ) : (
                <div className="w-full aspect-square bg-zinc-900 rounded-md flex items-center justify-center text-zinc-700"><ShoppingBag className="h-8 w-8" /></div>
              )}
              <div className="text-xs font-medium line-clamp-2 min-h-[2.5em]">{p.title}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {p.price && <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[10px] mono">{p.currency || "$"}{p.price}</Badge>}
                {p.commission_rate > 0 && <Badge variant="outline" className="border-emerald-700/60 text-emerald-300 text-[10px] mono">{p.commission_rate}%</Badge>}
                {p.voc_index > 1.5 && (
                  <Badge variant="outline" className="border-amber-700/60 text-amber-300 text-[10px] mono">
                    <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> VoC {p.voc_index.toFixed(1)}
                  </Badge>
                )}
              </div>
              <Button size="sm" onClick={() => importProduct(p)}
                      className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-xs mt-auto"
                      data-testid={`marketplace-import-${p.product_id}`}>
                <Download className="h-3 w-3 mr-1" /> Import + Generate
              </Button>
            </div>
          ))}
        </div>
      </PageBody>
    </>
  );
}
